import { Timestamp } from 'firebase/firestore';
import {
  chain, filter, findKey, get, isArray, isEmpty, isNaN, isNumber, map, some,
} from 'lodash';
import Time from 'src/mixins/Time';
import { formatDuration, parseDuration } from 'src/utils/DurationUtils';

export default {
  mixins: [Time],

  methods: {
    /**
     * Filter an array based on a filter object. The filter object can contains simple or
     * array properties that will be used for filtering. All filterObj properties are required
     * to exist if a filterObj property is an array then it will search for any of these values
     * @param {Array} searchArr Array of objects to search in
     * @param {Object} filterObj Object to use for filter.
     */
    filterDeep(searchArr, filterObj) {
      if (isEmpty(filterObj)) {
        return searchArr;
      }

      return filter(searchArr, (searchItems) => some(map(filterObj, (filterItems, filterKey) => {
        if (isArray(filterItems)) {
          return (filterItems.includes(searchItems[filterKey]));
        }
        return (searchItems[filterKey] === filterItems);
      })));
    }, // filterDeep()

    /**
     * If the new 'to' value is before the 'from' value, add a day to the 'to' value
     * this allows us to set availability slots that span across midnight and work
     * independently of the user's timezone
     * @param to Firestore Timestamp object
     * @param from Firestore Timestamp object
     */
    adjustToDate(to, from) {
      if (!(to instanceof Timestamp) || !(from instanceof Timestamp)) {
        return to;
      }

      let toDate = to.toDate();
      const fromDate = from.toDate();

      toDate = this.adjustDate(toDate, { day: this.getDate(fromDate) });
      if (fromDate >= toDate) {
        toDate = this.adjustDate(toDate, { day: this.getDate(toDate) + 1 });
      }
      toDate = this.adjustDate(toDate, {
        hour: this.getHours(to),
        minute: this.getMinutes(to),
        second: this.getSeconds(to),
      });

      return Timestamp.fromDate(toDate);
    }, // adjustToDate()

    /** Format duration (H:mm:ss) from duration in seconds. See utils/DurationUtils. */
    formatDuration(duration, includeHours = true) {
      return formatDuration(duration, includeHours);
    }, // formatDuration()

    formatDistance(distance, country) {
      if (!isNumber(distance) || isNaN(distance)) {
        return '-';
      }
      const roundedDistance = Math.round(distance * 10) / 10;
      return (['US', 'LR', 'MM'].includes(country))
        ? this.$tc('common.distance.imperial', roundedDistance, { distance: roundedDistance.toLocaleString() })
        : this.$tc('common.distance.metric', roundedDistance, { distance: roundedDistance.toLocaleString() });
    },

    stringToDuration(hms = '') {
      return parseDuration(hms);
    }, // stringToDuration()

    frequencyInfo(count, duration) {
      if (duration === 0) {
        return;
      }
      if (count >= duration) {
        return { factor: 1, unit: 'second' };
      }
      if ((count * 60) >= duration) {
        return { factor: 60, unit: 'minute' };
      }
      if ((count * 60 * 60) >= duration) {
        return { factor: 60 * 60, unit: 'hour' };
      }
      return { factor: 24 * 60 * 60, unit: 'day' };
    }, // frequencyInfo()

    formatFrequency(count, duration) {
      if (duration === 0) {
        return '-';
      }
      const { factor, unit } = this.frequencyInfo(count, duration);

      return `${Math.round(((count * factor) / duration) * 10) / 10} / ${unit}`;
    }, // formatFrequency()

    getFontAwesomeIconFromMIME(mimeType) {
      // List of official MIME Types: http://www.iana.org/assignments/media-types/media-types.xhtml
      const iconClasses = {
        // Media
        image: 'fa-file-image',
        audio: 'fa-file-audio',
        video: 'fa-file-video',
        // Hi Rasmus native files
        'hirasmus/form': 'fa-file-alt',
        // Documents
        'application/pdf': 'fa-file-pdf',
        'application/msword': 'fa-file-word',
        'application/vnd.ms-word': 'fa-file-word',
        'application/vnd.oasis.opendocument.text': 'fa-file-word',
        'application/vnd.openxmlformats-officedocument.wordprocessingml':
      'fa-file-word',
        'application/vnd.ms-excel': 'fa-file-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml':
      'fa-file-excel',
        'application/vnd.oasis.opendocument.spreadsheet': 'fa-file-excel',
        'application/vnd.ms-powerpoint': 'fa-file-powerpoint',
        'application/vnd.openxmlformats-officedocument.presentationml':
      'fa-file-powerpoint',
        'application/vnd.oasis.opendocument.presentation': 'fa-file-powerpoint',
        'text/plain': 'fa-file-alt',
        'text/html': 'fa-file-code',
        'application/json': 'fa-file-code',
        // Archives
        'application/gzip': 'fa-file-archive',
        'application/zip': 'fa-file-archive',
      };

      const icon = get(iconClasses, mimeType, false);
      if (icon !== false) {
        return icon;
      }

      return get(iconClasses, findKey(iconClasses, (item, key) => mimeType.indexOf(key) !== -1), 'fa-file');
    },

    naturalSort(collection, field, order = 'asc') {
      if (!isArray(collection)) {
        return;
      }

      collection.sort((a, b) => {
        const aField = a[field]?.toString() || '';
        const bField = b[field]?.toString() || '';

        return (order === 'asc'
          ? aField.localeCompare(bField, undefined, { numeric: true, sensitivity: 'base' })
          : bField.localeCompare(aField, undefined, { numeric: true, sensitivity: 'base' })
        );
      });

      return collection;
    }, // naturalSort()

    imageExtensionValidation(file) {
      const validExtensions = ['png', 'jpeg', 'jpg', 'avif', 'gif',
        'jfif', 'svg', 'webp', 'bmp', 'ico', 'cur'];
      let invalidExtMsg;
      let dotIndex;
      let ext;
      let filename;

      try {
        dotIndex = file.name.lastIndexOf('.');
        ext = dotIndex > -1 ? file.name.slice(dotIndex + 1) : '';
        filename = dotIndex > -1 ? file.name.slice(0, dotIndex) : file.name;

        if (
          (file.type.includes('image/') || this.isImage(ext))
            && !validExtensions.includes(ext.toLowerCase())
        ) {
          invalidExtMsg = this.$t('common.unsupportedImageMsg');
        }
      } catch (err) {
        console.error(`Error processing file: ${err}`);
        invalidExtMsg = this.$t('common.unsupportedImageError');
      }

      return { ext, filename, invalidExtMsg };
    }, // imageExtensionValidation()

    isImage(ext) {
      // os windows does not allow checking the file type
      const possibleImgExtensions = [
        'jpeg', 'jpg', 'jpe', 'jif', 'jfi', 'jfif', 'png', 'apng', 'avif', 'gif', 'svg', 'svgz',
        'bmp', 'dib', 'ico', 'cur', 'webp', 'tiff', 'tif', 'psd', 'raw', 'arw', 'cr2', 'nrw', 'k25',
        'heif', 'heic', 'ind', 'indd', 'indt', 'jp2', 'j2k', 'jpf', 'jpx', 'jpm', 'mj2', 'ai', 'eps',
      ];
      return possibleImgExtensions.includes(ext.toLowerCase());
    }, // isImage()

    isSameFieldValueForArrayEntries(array, iteratee) {
      return chain(array)
        .map(iteratee)
        .uniq()
        .size()
        .value() === 1;
    }, // isSameFieldValueForArrayEntries()
  },
};
